/**
 * useBuildFilterMeta — parallel to useShopFilterMeta. Seeds the /build
 * filter drawer with categories + catalog-wide price range.
 */

import { useCallback, useEffect, useState } from 'react'
import { APIBASE } from '../constants/api.constants'
import { APIROUTES } from '../constants/routes.constants'
import { isNetworkError, SERVER_UNAVAILABLE_MESSAGE } from '../utils/api/server-unavailable'

export interface BuildCategoryDto {
  readonly id: string
  readonly name: string
  readonly product_count: number
}

export interface BuildPriceRangeDto {
  readonly min_paisa: number | null
  readonly max_paisa: number | null
}

interface Body {
  readonly success: true
  readonly data: {
    readonly categories: BuildCategoryDto[]
    readonly priceRange: BuildPriceRangeDto
  }
}

export interface UseBuildFilterMetaReturn {
  readonly categories: BuildCategoryDto[]
  readonly priceRange: BuildPriceRangeDto
  readonly isLoading: boolean
  readonly error: string | null
  readonly reload: () => void
}

export function useBuildFilterMeta(): UseBuildFilterMetaReturn {
  const [categories, setCategories] = useState<BuildCategoryDto[]>([])
  const [priceRange, setPriceRange] = useState<BuildPriceRangeDto>({
    min_paisa: null,
    max_paisa: null
  })
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [tick, setTick] = useState(0)

  useEffect(() => {
    let cancelled = false
    const ac = new AbortController()
    setIsLoading(true)
    setError(null)
    ;(async () => {
      try {
        let res: Response
        try {
          res = await fetch(`${APIBASE}${APIROUTES.BUILDFILTERMETA}`, { signal: ac.signal })
        } catch (err) {
          if (cancelled) return
          if (isNetworkError(err)) throw new Error(SERVER_UNAVAILABLE_MESSAGE)
          throw err
        }
        if (cancelled) return
        if (res.status >= 500 || !res.ok) throw new Error(SERVER_UNAVAILABLE_MESSAGE)
        const body = (await res.json()) as Body
        if (!cancelled) {
          setCategories(body.data.categories)
          setPriceRange(body.data.priceRange)
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : SERVER_UNAVAILABLE_MESSAGE)
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    })()
    return () => { cancelled = true; ac.abort() }
  }, [tick])

  const reload = useCallback(() => setTick((t) => t + 1), [])
  return { categories, priceRange, isLoading, error, reload }
}
